/*
 * 苏州海管家物流科技有限公司（https://www.hgj.com/）.
 * 苏公网安备32059002003385号 © 2015-2023 Hgj.com All Rights Reserved. | 苏ICP备15030950号.
 * @Date: 2022.05.22 .
 * @Author
 * @Description 企微工作台 - 数据加载 axios 封装 .
 **/
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'

declare module 'axios' {
  interface AxiosRequestConfig {
    returnResponse?: boolean
  }
}
import { ElMessage } from '@hgj/element-plus'
import qs from 'qs'
import { axiosQwRequest, axiosQwResponse } from '@/utils'
import { getWebTrackerInstance } from '@hgj/storybrooke-business-tracker'
import { useProxyInsurance } from '@/hooks/useProxyInsurance'
const { getApprovalAgentHeader } = useProxyInsurance()
import { i18n } from '@/i18n'
import { expandHeader } from '@/utils/constant'
const { t } = i18n.global
const specialErrMsg: Record<string, string> = {
  '/saas-insurance/insures/hgj/clause': t('api.error.insuranceInfoFailed'),
}
const getSpecialMsg = (error: AxiosError) => {
  const url = error?.config?.url
  if (url && specialErrMsg[url]) {
    return specialErrMsg[url]
  }
  return error?.response?.data?.message || t('api.error.systemBusy')
}
// const appType = import.meta.env.VITE_APP_TYPE // 当前项目类型：qd：企点、企微：qw、套箱：tx

axios.defaults.headers.put['Content-Type'] = 'application/json;charset=UTF-8'
axios.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8'
axios.defaults.headers.delete['Content-Type'] = 'application/json;charset=UTF-8'

const request = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL, // 'https://dev-apisix.hgj.com/',
  timeout: 60 * 1000, // 请求超时时间
  withCredentials: true, // true：服务端设置Access-Control-Allow-Origin不能为 " * ", 必须是源地址
  headers: {
    appName: import.meta.env.VITE_APP_NAME,
    ...expandHeader,
  },

  transformRequest: [
    // 请求前的数据处理
    (data = {}, headers) => {
      // console.log('data--data-----------', data)
      // console.log('data--headers-----------', headers)
      if (headers && headers['Content-Type'] === 'multipart/form-data') {
        return data
      } else {
        return JSON.stringify(data)
      }
    },
    // ...axios.defaults.transformRequest
  ],
  /*  不同方式序列化 案例：
		// 请求数组 参数序列化
		qs.stringify({ids: [1, 2, 3]}, {indices: false})
		// 形式：ids=1&ids=2&id=3

		qs.stringify({ids: [1, 2, 3]}, {arrayFormat: 'indices'})
		// 形式：ids[0]=1&ids[1]=2&ids[2]=3

		qs.stringify({ids: [1, 2, 3]}, {arrayFormat: 'brackets'})
		// 形式：ids[]=1&ids[]=2&ids[]=3

		qs.stringify({ids: [1, 2, 3]}, {arrayFormat: 'repeat'})
		// 形式： ids=1&ids=2&id=3
   */
  // get
  paramsSerializer: (params) => {
    // console.log('get 才对 -params-----paramsSerializer-----------', params)
    return qs.stringify(params, { indices: false })
  },
})
/**
 *@Description 接口 监听 - 请求发送 - 请求前 .
 *@Return .
 **/
request.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    // 请求前的 参数 各项目 自行处理
    const obj = await axiosQwRequest()

    // 动态获取代投保企业请求头（确保非代投保企业时不添加）
    const approvalAgentHeader = getApprovalAgentHeader()

    config.headers = {
      ...config.headers,
      ...obj,
      ...approvalAgentHeader,
    }

    // console.log('request - config-----------', config)
    return config
  },
  (err: AxiosError) => {
    console.log('请求前：err', err)
    return Promise.reject(err)
  },
)
/**
 *@Description 接口监听 - 请求响应 - 返回值 .
 *@Return .
 **/
request.interceptors.response.use(
  async (response: AxiosResponse) => {
    // console.log('axios response 数据结果返回*****', response)

    const { code, message, msg, success, data } = response.data

    // 请求 返回 后的 数据 各项目 自行处理
    if (response.config.returnResponse) {
      return response
    }
    if (
      success ||
      code === 200 ||
      ['arraybuffer', 'blob'].includes(
        response.config.responseType?.toLowerCase() ?? '',
      )
    ) {
      // 上传 nas 无 suc
      return response.data
    }

    const m = msg || message || t('api.error.systemBusy')
    await axiosQwResponse(code, m, data, response) // 在 各自的 项目中 自行 处理 返回 结果 逻辑
  },
  (error: AxiosError) => {
    console.log('axios 返回结果 error：', error)
    // let { code, message } = error.response.data
    const tracker = getWebTrackerInstance()
    tracker?.track?.('responseError', '请求响应错误', 'error', {
      error: JSON.stringify(error),
    })
    if (!error?.response) {
      return false
    }

    const errMsg = getSpecialMsg(error)
    ElMessage.closeAll()
    ElMessage.error(errMsg)
    return Promise.reject(error)
  },
)

/**
 *@Description HTTP状态码判断 .
 *@Return .
 **/
// const errorHandle = (status: number, error): void => {
//     switch (status) {
//         case 401:
//             return console.log(`Error Code: ${status}, Message: ${error.msg || '登录失效，请重新登录'}`)
//         case 403:
//             return console.log(`Error Code: ${status}, Message: ${error.msg || '你没有访问权限'}`)
//         case 500:
//             return console.log(`Error Code: ${status}, Message: ${error.msg || '后台错误，请联系管理员'}`)
//         case 502:
//             return console.log(`Error Code: ${status}, Message: ${error.msg || '平台环境异常'}`)
//         default:
//             console.log(`Error Code: ${status}, Message: ${error.msg || '未知错误，请刷新重试'}`)
//     }
// }

/**
 *@Description 同时只会有一个请求，如果请求多次，会自动取消上一次的 request.
 *@Return .
 **/
const useSingleRequest = (request: any) => {
  if (!request?.defaults) {
    throw new Error('request is not a axios object')
  }

  const cancelObj: any = {}
  return (config: any) => {
    const key = `${config.method}:${config.url}`
    // 如果存在 取消函数 则执行 取消
    if (cancelObj[key]) {
      cancelObj[key]()
    }

    const params = {
      cancelToken: new axios.CancelToken((c) => {
        cancelObj[key] = c
      }),
      ...config,
    }
    return request(params)
  }
}

// 同时只会有一个请求，如果请求多次，会自动取消上一次的request
export const singleRequest = useSingleRequest(request)

export default request
